import { usePathname } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { isApiError } from "@/app/common-types/api-error.types";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import { QuestionThree } from "@/config/app-constant";
import { useQuestionsState } from "@/providers/questions-state-provider";
import { RootState } from "@/store/root-reducer";
import { STCountryArray } from "@/types/QuestionPayloadType";
import { QuotePayload } from "@/types/quote/quote-data";
import { getSubmitButtonState } from "@/utils/question-location-to.utils";
import { LocationToFormContent } from "@/utils/schema/location-to-page-schema.utils";

import LocationToContainer from "./location-to-container";

type LocationToFormContentProps = {
  pageContent: LocationToFormContent;
};
function LocationToFormSection({
  pageContent,
}: Readonly<LocationToFormContentProps>) {
  const quoteResponse = useSelector((state: RootState) => state.quote);
  const modifiedQuoteResponse = useMemo(
    () =>
      (isApiError(quoteResponse)
        ? quoteResponse.response?.data
        : quoteResponse) as QuotePayload,
    [quoteResponse]
  );
  const [toLocationCountryDetails, setToLocationCountryDetails] = useState<
    STCountryArray | undefined
  >(undefined);
  const policyType = useSelector(
    (state: RootState) => state.quote?.attributes?.tripDetails?.policyType
  );
  const { selected, setSelectedState } = useQuestionsState();
  const pathname = usePathname();
  const { current } = useWizard();

  const handleQuestionThreePath = useCallback(
    (quoteResponseData: QuotePayload | null) => {
      if (policyType === "ANNUAL_MULTI_TRIP") {
        const toLocation = quoteResponseData?.attributes?.tripDetails?.area;
        if (toLocation) setSelectedState(toLocation);
      } else {
        const toLocation =
          quoteResponseData?.attributes?.tripDetails?.toLocation;
        if (toLocation) setSelectedState(toLocation);
        setToLocationCountryDetails(
          quoteResponseData?.attributes?.tripDetails?.toLocationCountryDetails
        );
      }
    },
    [policyType, setSelectedState]
  );

  const handleSelected = useCallback(
    (quoteResponseData: QuotePayload | null) => {
      if (!quoteResponse) return;

      if (pathname.replace(/(^\/)|(\/$)/g, "") === current?.id) {
        handleQuestionThreePath(quoteResponseData);
      }
    },
    [quoteResponse, pathname, current?.id, handleQuestionThreePath]
  );

  useEffect(() => {
    handleSelected(quoteResponse);
  }, [handleSelected, quoteResponse]);

  return (
    <LocationToContainer
      tripDetails={modifiedQuoteResponse?.attributes?.tripDetails ?? {}}
      usagePage={QuestionThree}
      pageContent={pageContent}
      stateFromLocation={
        modifiedQuoteResponse?.attributes?.tripDetails?.fromLocation ?? ""
      }
      toLocationCountryDetails={toLocationCountryDetails}
      selectedpolicyType={policyType ?? ""}
      buttonDisable={getSubmitButtonState(
        policyType ?? "",
        (selected as string) || "",
        current.uiElementControls?.locationTo ?? false
      )}
      setToLocationCountryDetails={setToLocationCountryDetails}
    />
  );
}

export default LocationToFormSection;
